import { css } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import { sortBy } from "lodash";
import * as React from "react";
import { UseLabelStrategy } from "../../../utils/LabelStrategy";
import { FilterSearchBox } from "../../FilterSearchBox";
import type { FormFieldProps } from "../FormFieldProps";
import type { DeprecatedDropdownMenuOption } from "./DeprecatedDropDownMenu";
import { DeprecatedSelectField } from "./DeprecatedSelectField";
const styles = {
    select: css({
        width: "100% !important",
        minWidth: "0 !important",
        overflow: "visible",
        display: "flex",
        flexDirection: "column",
    }),
    filter: css({
        margin: `0 ${space[16]}`,
    }),
    container: css({
        width: "100%",
    }),
};
export interface OtherSelectProps {
    items: DeprecatedDropdownMenuOption[];
    placeholder?: string;
    allowClear?: boolean;
    allowFilter?: boolean;
    autoFocus?: boolean;
    disabled?: boolean;
    label?: string | JSX.Element;
    warning?: string | null;
    error?: string | null;
    empty?: string;
    selectionRenderer?: (value: string, menuItem: DeprecatedDropdownMenuOption) => React.ReactNode;
    validate?(value: string): string;
    onFilterChanged?(value: string): Promise<DeprecatedDropdownMenuOption[]>;
    sortItems?: boolean;
    inlineButton?: React.ReactNode;
    /**
     * Temporary Prop from FFT to handle the differences between level 1 and level 2 page layouts.
     */
    pageLayoutV2?: boolean;
}
export type SelectProps = OtherSelectProps & FormFieldProps<string | undefined>;
export function sortDropDownItems(items: DeprecatedDropdownMenuOption[]): DeprecatedDropdownMenuOption[] {
    // Respect items with numeric prefixes, like Step names - eg "1. Do thing", "2. Do another", "10. Do a tenth thing". If doing purely string sort order, the "10" would end up in the wrong place.
    return sortBy(items, [(x) => parseInt(x.text ?? "") || x.text?.toLowerCase(), (x) => x.text?.toLowerCase()]);
}
interface SelectState {
    error?: string;
    showExternalError: boolean;
    filter?: string;
    filteredItems: DeprecatedDropdownMenuOption[];
}
class DeprecatedSelectInternal extends React.Component<OtherSelectProps & FormFieldProps<string | undefined>, SelectState> {
    constructor(props: OtherSelectProps & FormFieldProps<string | undefined>) {
        super(props);
        this.state = {
            filter: undefined,
            filteredItems: [],
            error: undefined,
            showExternalError: true,
        };
    }
    componentDidUpdate(prevProps: Readonly<OtherSelectProps & FormFieldProps<string | undefined>>) {
        if (this.props.value !== prevProps.value) {
            this.setState({ showExternalError: false });
        }
        const isNewExternalErrorAvailable = prevProps.error !== this.props.error;
        if (isNewExternalErrorAvailable) {
            this.setState({ showExternalError: true });
        }
    }
    render() {
        const { value, label, items, error, onChange, validate, placeholder, allowClear, allowFilter, sortItems = true, disabled, empty, selectionRenderer, onFilterChanged, warning, autoFocus, helperText, inlineButton, pageLayoutV2 } = this.props;
        const applicableItems = this.state.filteredItems.length > 0 ? this.state.filteredItems : items;
        const itemsInList = sortItems ? sortDropDownItems(applicableItems) : applicableItems;
        const errorText = this.state.error ?? (this.state.showExternalError ? error : undefined) ?? warning ?? undefined;
        return (<div className={styles.container}>
                <DeprecatedSelectField className={styles.select} value={value} selectedValueAccessibleName={items.find((i) => i.value === value)?.text ?? value} allowClear={allowClear} onChange={this.handleChange} floatingLabelText={label} placeholder={placeholder} errorText={errorText} onCloseDropdown={this.handleOnClose} filter={allowFilter && (<div className={styles.filter}>
                                <FilterSearchBox placeholder={"Find..."} autoFocus={true} value={this.state.filter} onChange={this.handleFilterChanged} fullWidth={true}/>
                            </div>)} disabled={disabled} selectionRenderer={selectionRenderer} autoFocus={autoFocus} items={itemsInList} empty={empty} inlineButton={inlineButton} pageLayoutV2={pageLayoutV2}/>
            </div>);
    }
    private handleChange = (value: string) => {
        if (this.props.validate) {
            const result = this.props.validate(value);
            this.setState((prev) => ({ ...prev, error: result }));
        }
        this.setState((prev) => ({
            ...prev,
            filter: undefined,
            filteredItems: [],
        }));
        if (this.props.onChange) {
            this.props.onChange(value);
        }
    };
    private handleFilterChanged = async (value: string) => {
        let filteredItems: DeprecatedDropdownMenuOption[] = [];
        if (this.props.onFilterChanged) {
            filteredItems = await this.props.onFilterChanged(value);
        }
        else {
            filteredItems = this.props.items.filter((item) => {
                return item.text.toLowerCase().search(value.toLowerCase()) !== -1;
            });
        }
        this.setState((prev) => {
            return {
                ...prev,
                filter: value,
                filteredItems,
            };
        });
    };
    private handleOnClose = () => {
        if (this.state.filter) {
            this.setState({
                filter: undefined,
                filteredItems: [],
            });
        }
    };
    static displayName = "DeprecatedSelectInternal";
}
/**
 * @deprecated
 * Deprecated: 22/01/2026
 * Replacement: Select component from @octopusdeploy/design-system-components
 * Reason: This component is deprecated and will be removed in a future release.
 */
export const DeprecatedSelect = UseLabelStrategy(DeprecatedSelectInternal, (fieldName) => `Select ${fieldName}`);
export { DeprecatedSelectInternal };
